"use client"

import React, { useState } from "react"
import { Popover, PopoverContent, PopoverTrigger } from "../shadcn/popover"

/**
 * 팝오버 (부유 패널)
 *
 * @description
 * 클릭으로 열리는 부유 패널입니다. 필터, 설정 패널 등 복잡한 내용을 표시할 때 사용합니다.
 * 두 가지 API를 지원합니다:
 *
 * **1. trigger + children API (권장)** — 평탄한 JSX로 에이전트 친화적
 *
 * **2. renderButton + renderPopover API (기존)** — onClose를 수동 호출
 *
 * @example
 * ```tsx
 * // 1. trigger + children API (권장)
 * <TPopover trigger={<TButton color="ghost">필터</TButton>}>
 *   <div className="p-4 w-[300px]">
 *     <TText variant="subtitle">필터 옵션</TText>
 *   </div>
 * </TPopover>
 *
 * // 2. renderButton + renderPopover API (기존)
 * <TPopover
 *   renderButton={({ open }) => (
 *     <TButton color="ghost" selected={open}>필터</TButton>
 *   )}
 *   renderPopover={({ onClose }) => (
 *     <div className="p-4 w-[300px]">
 *       <TText variant="subtitle">필터 옵션</TText>
 *       <TButton onClick={onClose}>적용</TButton>
 *     </div>
 *   )}
 * />
 * ```
 */
interface TPopoverProps {
	/**
	 * 팝오버 트리거 요소 (trigger + children API용)
	 * trigger가 있으면 children을 팝오버 내용으로 사용
	 */
	trigger?: React.ReactNode
	/**
	 * 팝오버 내용 (trigger API용) — JSX를 직접 배치
	 */
	children?: React.ReactNode
	/**
	 * 팝오버 트리거 버튼 렌더링 (renderButton + renderPopover API용)
	 * @param props.open 팝오버 열림 상태
	 * @param props.onOpen 팝오버 열기 함수 (수동 제어 시)
	 * @param props.disabled 비활성 상태
	 */
	renderButton?: (props: { open?: boolean; onOpen: () => void; disabled?: boolean }) => React.ReactNode
	/**
	 * 팝오버 내용 렌더링 (renderButton + renderPopover API용)
	 * @param props.onClose 팝오버 닫기 함수
	 */
	renderPopover?: (props: { onClose: () => void }) => React.ReactNode
	/** 추가 CSS 클래스 */
	className?: string
	/** 팝오버 정렬 @default "start" */
	align?: "start" | "center" | "end"
	/** 팝오버 방향 @default "bottom" */
	side?: "top" | "right" | "bottom" | "left"
	/** 정렬 오프셋 @default 0 */
	alignOffset?: number
	/** 방향 오프셋 @default 4 */
	sideOffset?: number
	/** 비활성 상태 */
	disabled?: boolean
	/** 모달 모드 (팝오버 외부 클릭 시 닫힘 방지) @default false */
	modal?: boolean
	/** 제어 모드용 열림 상태 */
	open?: boolean
	/** 팝오버 열릴 때 콜백 */
	onPopoverOpen?: () => void
	/** 팝오버 닫힐 때 콜백 */
	onPopoverClose?: () => void
}

export const TPopover = React.memo((props: TPopoverProps & { container?: React.RefObject<HTMLElement> }) => {
	const {
		trigger,
		children,
		renderButton,
		renderPopover,
		className,
		align = "start",
		side = "bottom",
		alignOffset = 0,
		sideOffset = 4,
		disabled,
		container,
		modal = false,
		open,
		onPopoverOpen,
		onPopoverClose
	} = props

	const [isOpen, setIsOpen] = useState(false)

	const handleOpen = React.useCallback(() => {
		setIsOpen(true)
		onPopoverOpen?.()
	}, [onPopoverOpen])

	const handleClose = React.useCallback(() => {
		setIsOpen(false)
		onPopoverClose?.()
	}, [onPopoverClose])

	const onOpenChange = React.useCallback(
		(open: boolean) => {
			if (open) {
				handleOpen()
			} else {
				handleClose()
			}
		},
		[handleOpen, handleClose]
	)

	const handleEscapeKeyDown = React.useCallback(
		(e: KeyboardEvent) => {
			e.preventDefault()
			handleClose()
		},
		[handleClose]
	)

	// trigger + children API vs renderButton + renderPopover API
	const useTriggerAPI = trigger !== undefined

	if (process.env.NODE_ENV === "development" && useTriggerAPI && (renderButton || renderPopover)) {
		console.warn("TPopover: trigger와 renderButton/renderPopover를 동시에 사용할 수 없습니다. trigger API가 우선 적용됩니다.")
	}

	const triggerContent = useTriggerAPI ? trigger : renderButton?.({ open: open ?? isOpen, onOpen: handleOpen, disabled })
	const popoverContent = useTriggerAPI ? children : renderPopover?.({ onClose: handleClose })

	return (
		<Popover open={(open ?? isOpen) && !disabled} onOpenChange={onOpenChange} modal={modal}>
			<PopoverTrigger asChild disabled={disabled}>
				<div>{triggerContent}</div>
			</PopoverTrigger>
			<PopoverContent
				container={container?.current ?? undefined}
				className={className}
				align={align}
				side={side}
				alignOffset={alignOffset}
				sideOffset={sideOffset}
				onEscapeKeyDown={handleEscapeKeyDown}
			>
				{popoverContent}
			</PopoverContent>
		</Popover>
	)
})

TPopover.displayName = "TPopover"
